<script lang="ts">
	import { page } from '$app/state';
	import { translateOptionTitle } from '$lib';
	import { m } from '$lib/paraglide/messages';
	import { SvelteMap } from 'svelte/reactivity';

	import Price from '../Price.svelte';

	const {
		options,
		selectedOptions = $bindable(),
	}: {
		options: ProductOption[];
		selectedOptions: SvelteMap<ProductOption | ProductSubOption, string | ProductOptionValue>;
	} = $props();

	const shouldShowSubOption = (sub: ProductSubOption) => {
		return sub.parent_ids.some((pid) =>
			Array.from(selectedOptions.values()).some((v) => typeof v === 'object' && v.id === pid),
		);
	};

	const setValue = (
		option: ProductOption | ProductSubOption,
		value: ProductOptionValue | string | null,
	) => {
		if (value === null) {
			selectedOptions.delete(option);
		} else {
			selectedOptions.set(option, value);
		}

		if ('sub_options' in option && option.sub_options) {
			resetSubValues(option);
		}
	};

	const resetSubValues = (option: ProductOption) => {
		option.sub_options.forEach((sub) => selectedOptions.delete(sub));
	};
</script>

<div class="flex flex-col gap-2 md:max-h-none">
	{#each options as option (option.id)}
		{@render optionInput(option, option.is_required)}
		{#each option.sub_options as subOption (subOption.id)}
			{#if shouldShowSubOption(subOption)}
				{@render optionInput(subOption, true)}
			{/if}
		{/each}
	{/each}
</div>

{#snippet optionInput(option: ProductOption | ProductSubOption, isRequired: boolean)}
	{#if option.type === 'text'}
		<label>
			{@render label(option, isRequired)}
			<input
				type="text"
				name="option-{option.id}"
				required={isRequired}
				value={selectedOptions.get(option)}
				oninput={(e) => setValue(option, e.currentTarget.value)}
				oninvalid={(e) => e.preventDefault()}
				autocomplete="off"
				class="peer block w-full rounded-md border border-neutral-300 p-2 text-sm user-invalid:border-red-600"
				maxlength={option.max_characters}
			/>
			<div class="text-xs">
				{@html m.max_characters({ max: option.max_characters })}
			</div>
			{@render errorMessage(isRequired)}
		</label>
	{:else if option.type === 'drop_down'}
		<label>
			{@render label(option, isRequired)}
			<select
				name="option-{option.id}"
				required={isRequired}
				oninput={(e) =>
					setValue(
						option,
						e.currentTarget.value !== ''
							? option.values.find((v) => v.id === Number(e.currentTarget.value))!
							: null,
					)}
				oninvalid={(e) => e.preventDefault()}
				autocomplete="off"
				class="peer block w-full rounded-md border border-neutral-300 p-2.5 text-sm user-invalid:border-red-600"
			>
				<option disabled={isRequired} value={null}>-- {m.choose_an_option()} --</option>
				{#each option.values as value (value.id)}
					<option
						value={value.id}
						disabled={!value.available}
						selected={selectedOptions.get(option) === value}
					>
						{translateOptionTitle(page.data.store.language, value)}
						{#if value.price > 0}
							+<Price price={value.price} />
						{/if}
					</option>
				{/each}
			</select>
			{@render errorMessage(isRequired)}
		</label>
	{:else if option.type === 'radio' || option.type === 'checkbox'}
		<div>
			{@render label(option, isRequired)}
			<div class="flex flex-wrap gap-2">
				{#each option.values as value (value.id)}
					<label
						class="flex w-full cursor-pointer gap-2 bg-neutral-100 p-2 text-sm hover:bg-neutral-200 lg:w-[calc(50%-0.5rem)]"
					>
						<input
							type={option.type}
							name="option-{option.id}"
							required={isRequired}
							value={value.id}
							oninput={(e) => setValue(option, e.currentTarget.checked ? value : null)}
							oninvalid={(e) => e.preventDefault()}
							checked={selectedOptions.get(option) === value}
							autocomplete="off"
							class="peer user-invalid:border-red-600"
						/>
						<div>
							{translateOptionTitle(page.data.store.language, value)}
							{#if value.price > 0}
								<span class="text-gray-400">
									+<Price price={value.price} />
								</span>
							{/if}
						</div>
						{@render errorMessage(isRequired)}
					</label>
				{/each}
			</div>
		</div>
	{/if}
{/snippet}

{#snippet label(option: ProductOption | ProductSubOption, isRequired: boolean)}
	<span class="text-xs">
		{translateOptionTitle(page.data.store.language, option)}
		{#if option.price && option.price > 0}
			+<Price price={option.price} />
		{/if}
		{#if isRequired}
			<span class="text-red-600">*</span>
		{/if}
	</span>
{/snippet}
{#snippet errorMessage(isRequired: boolean)}
	{#if isRequired}
		<div class="mt-1 hidden text-xs text-red-600 peer-user-invalid:block">
			{m.this_field_is_required()}
		</div>
	{/if}
{/snippet}
